import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { Select, Table, useLocalizeMessage } from 'libs.nucleus';
import { getTemplateDisplayNames } from './sites_table.component';
import { getAssignedEnvironmentSiteIds } from '../mom_adapter/assignment_join';
const DocumentSitesSelectCell = ({ documentId, environmentSites, assignments, onAssignedEnvironmentSitesChange, }) => {
    const translate = useLocalizeMessage();
    const options = useMemo(() => environmentSites.map((site) => ({
        value: String(site.id),
        label: site.name ?? site.number ?? site.key ?? String(site.id),
    })), [environmentSites]);
    const selectedOptions = useMemo(() => {
        const selectedSiteIds = new Set(getAssignedEnvironmentSiteIds(assignments, documentId));
        return options.filter((option) => selectedSiteIds.has(Number(option.value)));
    }, [assignments, documentId, options]);
    const selectionLabel = useMemo(() => {
        if (selectedOptions.length === 0) {
            return undefined;
        }
        if (selectedOptions.length === 1) {
            return selectedOptions[0]?.label;
        }
        return `(${selectedOptions.length}) ${selectedOptions.map((o) => o.label).join(', ')}`;
    }, [selectedOptions]);
    const handleChange = useCallback((selectedOptions) => {
        onAssignedEnvironmentSitesChange(documentId, selectedOptions.map((option) => Number(option.value)), environmentSites);
    }, [documentId, environmentSites, onAssignedEnvironmentSitesChange]);
    const disabled = environmentSites.length === 0;
    const sitesForDocumentAriaLabel = translate('Sites for document');
    return (_jsx(Select, { multiple: true, "aria-label": sitesForDocumentAriaLabel, options: options, value: selectedOptions, onChange: handleChange, selectionLabel: selectionLabel, placeholder: translate('Select sites'), width: 'full', disabled: disabled, showMultipleSelectionBadges: false, dataTestId: `site-configuration-icf-sites-select-${documentId}` }));
};
export const IcfDocumentsTable = ({ consentFiles, environmentSites, assignments, labelNoResults, hideUnassignedRows = false, hideAssignedRows = false, onAssignedEnvironmentSitesChange, }) => {
    const translate = useLocalizeMessage();
    // MOM stores assignments by templateKey (lineage), so surface one row per lineage using the
    // same Published > In Review > Draft priority as the SitesTable dropdown.
    const dedupedConsentFiles = useMemo(() => getTemplateDisplayNames(consentFiles), [consentFiles]);
    const visibleConsentFiles = useMemo(() => {
        if (!hideUnassignedRows && !hideAssignedRows) {
            return dedupedConsentFiles;
        }
        const assignedDocumentIds = new Set(assignments.map((a) => a.consentDocumentId));
        return dedupedConsentFiles.filter((file) => hideUnassignedRows ? assignedDocumentIds.has(file.id) : !assignedDocumentIds.has(file.id));
    }, [assignments, dedupedConsentFiles, hideAssignedRows, hideUnassignedRows]);
    const columns = useMemo(() => [
        { header: translate('Name'), accessorKey: 'title' },
        { header: translate('Version'), accessorKey: 'version' },
        { header: translate('Status'), accessorKey: 'status' },
        { header: translate('ID'), accessorKey: 'id' },
        {
            id: 'icf-environment-sites',
            header: translate('Sites'),
            cell: (context) => (_jsx(DocumentSitesSelectCell, { documentId: context.row.original.id, environmentSites: environmentSites, assignments: assignments, onAssignedEnvironmentSitesChange: onAssignedEnvironmentSitesChange }, context.row.original.id)),
        },
    ], [translate, environmentSites, assignments, onAssignedEnvironmentSitesChange]);
    return (_jsx("div", { "data-testid": 'site-configuration-icf-documents-table', children: _jsx(Table, { columns: columns, data: visibleConsentFiles, getRowId: (row) => row.id, labelNoResults: labelNoResults }) }));
};
